/**
 * Chat panel — bottom-left overlay.  Shows recent messages (Issue #9),
 * an input line that opens with Enter, sends with Enter, closes with
 * Escape.  Up/Down arrows navigate history.
 */

import type { ChatLine } from '@hurka/shared';
import { MAX_CHAT_LEN } from '@hurka/shared';

export interface ChatPanelHandle {
  pushLine(line: ChatLine): void;
  pushSystem(text: string): void;
  isOpen(): boolean;
  open(): void;
  close(): void;
  toggle(): void;
  setOnSubmit(cb: (text: string) => void): void;
  dispose(): void;
}

const MAX_HISTORY = 12;

export function createChatPanel(): ChatPanelHandle {
  let isOpen = false;
  let onSubmit: ((text: string) => void) | null = null;
  const history: string[] = [];
  let historyIdx = -1;

  const root = document.createElement('div');
  root.id = 'chat';
  Object.assign(root.style, {
    position: 'fixed',
    bottom: '8px',
    left: '8px',
    width: '380px',
    maxHeight: '180px',
    background: 'rgba(0,0,0,0.55)',
    border: '1px solid #5a4a30',
    fontFamily: '"Courier New", monospace',
    fontSize: '13px',
    color: '#c0c0c0',
    zIndex: '20',
    pointerEvents: 'none',
    userSelect: 'none',
  } as CSSStyleDeclaration);

  const logEl = document.createElement('div');
  logEl.id = 'chat-log';
  Object.assign(logEl.style, {
    padding: '6px 8px',
    overflowY: 'hidden',
    lineHeight: '1.4',
    maxHeight: '120px',
  } as CSSStyleDeclaration);

  const inputEl = document.createElement('input');
  inputEl.id = 'chat-input';
  inputEl.maxLength = MAX_CHAT_LEN;
  inputEl.placeholder = 'press Enter to chat…';
  Object.assign(inputEl.style, {
    width: '100%',
    padding: '4px 8px',
    background: 'rgba(0,0,0,0.7)',
    border: 'none',
    borderTop: '1px solid #5a4a30',
    color: '#fff',
    fontFamily: 'inherit',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    display: 'none',
  } as CSSStyleDeclaration);

  root.appendChild(logEl);
  root.appendChild(inputEl);
  document.body.appendChild(root);

  function renderLine(line: ChatLine | { system: string }): void {
    const div = document.createElement('div');
    if ('system' in line) {
      div.style.color = '#7a6a4a';
      div.style.fontStyle = 'italic';
      div.textContent = `* ${line.system}`;
    } else {
      const fromSpan = document.createElement('span');
      fromSpan.style.color = '#c0a060';
      fromSpan.textContent = `${line.from}: `;
      div.appendChild(fromSpan);
      div.appendChild(document.createTextNode(line.text));
    }
    logEl.appendChild(div);
    while (logEl.children.length > MAX_HISTORY) logEl.removeChild(logEl.firstChild!);
  }

  function submit(): void {
    const text = inputEl.value.trim();
    if (text && onSubmit) {
      onSubmit(text);
      if (!history.includes(text)) history.push(text);
      if (history.length > 50) history.shift();
      inputEl.value = '';
    }
    historyIdx = -1;
    closePanel();
  }

  inputEl.addEventListener('keydown', (e) => {
    const ke = e as KeyboardEvent;
    if (ke.key === 'Enter') {
      ke.preventDefault();
      submit();
    } else if (ke.key === 'Escape') {
      ke.preventDefault();
      closePanel();
    } else if (ke.key === 'ArrowUp') {
      ke.preventDefault();
      if (history.length === 0) return;
      historyIdx = Math.min(history.length - 1, historyIdx + 1);
      inputEl.value = history[history.length - 1 - historyIdx] ?? '';
    } else if (ke.key === 'ArrowDown') {
      ke.preventDefault();
      if (historyIdx <= 0) {
        historyIdx = -1;
        inputEl.value = '';
      } else {
        historyIdx--;
        inputEl.value = history[history.length - 1 - historyIdx] ?? '';
      }
    }
  });

  function openPanel(): void {
    if (isOpen) return;
    isOpen = true;
    inputEl.style.display = 'block';
    inputEl.focus();
  }

  function closePanel(): void {
    if (!isOpen) return;
    isOpen = false;
    inputEl.style.display = 'none';
    inputEl.blur();
  }

  return {
    pushLine(line) {
      renderLine(line);
    },
    pushSystem(text) {
      renderLine({ system: text });
    },
    isOpen: () => isOpen,
    open: openPanel,
    close: closePanel,
    toggle() {
      if (isOpen) closePanel();
      else openPanel();
    },
    setOnSubmit(cb) {
      onSubmit = cb;
    },
    dispose() {
      root.remove();
    },
  };
}
